import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from './ui/tooltip';
import {
  X,
  Download,
  Pencil,
  Trash2,
  Maximize2,
  ZoomIn,
  ZoomOut,
  Image,
  FileText,
  Table2,
  Video,
  Music,
  Archive,
  Code,
  File,
  Info,
  Loader2
} from 'lucide-react';
import { useMediaQuery } from '../hooks/useMediaQuery';
import fileService from '../services/fileService';

const FilePreviewComponent = ({
  file,
  open,
  onClose,
  onDownload,
  onEdit,
  onDelete,
  showActions = true,
}) => {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [previewData, setPreviewData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  // Load preview data
  const loadPreview = useCallback(async () => {
    if (!file || !file.canPreview) return;

    try {
      setLoading(true);
      setError(null);

      // For images, create blob URL for preview
      if (isImageFile(file.filename)) {
        const response = await fetch(`/api/files/${file.file_id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load image');
        }

        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setPreviewData({ type: 'image', url: imageUrl });
      }
      // For text files, fetch content
      else if (isTextFile(file.filename)) {
        const response = await fetch(`/api/files/${file.file_id}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load text file');
        }

        const text = await response.text();
        setPreviewData({ type: 'text', content: text });
      }
      // For PDFs, fetch with authentication and create blob URL for preview
      else if (isPdfFile(file.filename)) {
        const response = await fetch(`/api/files/${file.file_id}?inline=true`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to load PDF');
        }

        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        setPreviewData({ type: 'pdf', url: pdfUrl });
      }
    } catch (err) {
      console.error('Preview error:', err);
      setError('预览加载失败');
    } finally {
      setLoading(false);
    }
  }, [file]);

  // Reset state when file changes
  useEffect(() => {
    if (file) {
      setPreviewData(null);
      setError(null);
      setZoom(1);
      setFullscreen(false);
      loadPreview();
    }
  }, [file, loadPreview]);

  // Clean up blob URLs for images and PDFs
  useEffect(() => {
    return () => {
      if (previewData?.url && previewData.url.startsWith('blob:')) {
        URL.revokeObjectURL(previewData.url);
      }
    };
  }, [previewData]);

  // File type checks
  const isImageFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext);
  };

  const isTextFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ['txt', 'csv', 'json', 'xml', 'js', 'css', 'html', 'md'].includes(ext);
  };

  const isPdfFile = (filename) => {
    const ext = filename.toLowerCase().split('.').pop();
    return ext === 'pdf';
  };

  // Get file icon
  const getFileIcon = (filename, category) => {
    const iconClass = 'h-12 w-12 text-muted-foreground';

    switch (category || fileService.getFileCategory(filename)) {
      case 'image':
        return <Image className={`${iconClass} text-primary`} />;
      case 'document':
        return <FileText className={`${iconClass} text-destructive`} />;
      case 'spreadsheet':
        return <Table2 className={`${iconClass} text-success`} />;
      case 'video':
        return <Video className={`${iconClass} text-secondary-foreground`} />;
      case 'audio':
        return <Music className={`${iconClass} text-warning`} />;
      case 'archive':
        return <Archive className={`${iconClass} text-info`} />;
      case 'code':
        return <Code className={`${iconClass} text-primary`} />;
      default:
        return <File className={iconClass} />;
    }
  };

  // Render preview content
  const renderPreviewContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center min-h-[200px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      );
    }

    if (error) {
      return (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      );
    }

    if (!file.canPreview || !previewData) {
      return (
        <div className="text-center py-8">
          {getFileIcon(file.filename, file.category)}
          <h3 className="text-lg font-semibold mt-4">
            无法预览此文件
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            文件类型不支持在线预览
          </p>
          <Button onClick={onDownload}>
            <Download className="h-4 w-4 mr-2" />
            下载文件
          </Button>
        </div>
      );
    }

    // Render based on preview type
    switch (previewData.type) {
      case 'image':
        return (
          <div className="text-center">
            <div className="mb-4 flex justify-center gap-2">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setZoom(prev => Math.min(prev + 0.25, 3))}
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>放大</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setZoom(prev => Math.max(prev - 0.25, 0.25))}
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>缩小</TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setZoom(1)}
                    >
                      {Math.round(zoom * 100)}%
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>重置缩放</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="max-w-full max-h-[70vh] overflow-auto border rounded-md p-2 bg-muted">
              <img
                src={previewData.url}
                alt={file.filename}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  transform: `scale(${zoom})`,
                  transition: 'transform 0.2s ease-in-out',
                }}
              />
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="max-h-[70vh] overflow-auto border rounded-md p-4 bg-muted font-mono text-sm leading-relaxed">
            <pre className="m-0 whitespace-pre-wrap break-words">
              {previewData.content}
            </pre>
          </div>
        );

      case 'pdf':
        return (
          <div className="h-[70vh] border rounded-md overflow-hidden">
            <iframe
              src={`${previewData.url}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title={file.filename}
            />
          </div>
        );

      default:
        return null;
    }
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className={`${isMobile || fullscreen ? 'max-w-full h-full' : 'max-w-4xl max-h-[90vh]'} overflow-hidden flex flex-col`}>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              {getFileIcon(file.filename, file.category)}
              <div className="min-w-0">
                <DialogTitle className="truncate">{file.filename}</DialogTitle>
                <p className="text-sm text-muted-foreground">
                  {file.displaySize} • {file.categoryDisplay}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              {!isMobile && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setFullscreen(!fullscreen)}
                      >
                        <Maximize2 className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{fullscreen ? '退出全屏' : '全屏'}</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto px-1 py-4">
          {/* File Info */}
          <Card className="mb-4">
            <CardContent className="py-4">
              <div className="flex items-center gap-2 mb-3">
                <Info className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium">文件信息</span>
              </div>

              <div className="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">文件名</p>
                  <p className="text-sm">{file.filename}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">文件大小</p>
                  <p className="text-sm">{file.displaySize}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">文件类型</p>
                  <p className="text-sm">{file.categoryDisplay}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">上传时间</p>
                  <p className="text-sm">
                    {new Date(file.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              {file.description && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <p className="text-xs text-muted-foreground">描述</p>
                    <p className="text-sm">{file.description}</p>
                  </div>
                </>
              )}

              {file.tags && file.tags.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">标签</p>
                    <div className="flex gap-1 flex-wrap">
                      {file.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Preview Content */}
          {renderPreviewContent()}
        </div>

        {showActions && (
          <DialogFooter className="flex-row justify-between gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>关闭</Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onDownload}>
                <Download className="h-4 w-4 mr-2" />
                下载
              </Button>
              {onEdit && (
                <Button variant="outline" onClick={onEdit}>
                  <Pencil className="h-4 w-4 mr-2" />
                  编辑
                </Button>
              )}
              {onDelete && (
                <Button variant="outline" className="text-destructive hover:text-destructive" onClick={onDelete}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  删除
                </Button>
              )}
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewComponent;
